import React from 'react'
import { useTranslation } from 'react-i18next'
import { Avatar, Box, Button, Card, Icon } from '@node-space/ui-components'
import {
  PaymentLimitAction,
  type GroupedLimit,
  type Limit,
  type PaymentLimitActionType,
} from 'types/paymentLimits'
import { roundNumberWithCommas } from 'utils/utils'
import { formatPaymentMethodLabel, LIMIT_TYPE_LABELS } from '../utils'

interface PaymentLimitCardProps {
  groupedLimit: GroupedLimit
  isDeleting?: boolean
  onAction?: (action: PaymentLimitActionType, limit: Limit) => void
}

export const PaymentLimitCard = ({ groupedLimit, isDeleting, onAction }: PaymentLimitCardProps) => {
  const { t } = useTranslation()
  const { method, currency, transactionLimit, dailyLimit } = groupedLimit

  const hasBothLimits = !!(transactionLimit && dailyLimit)

  return (
    <Card variant="static" emphasis full className="py-6">
      <Box flexCol className="h-[190px]">
        <h3 className="text-h3 text-foreground-prominent">{formatPaymentMethodLabel(method)}</h3>

        <Box flex align="center" gap={8} className="mt-1 mb-4">
          <Avatar name={currency} type="currency" size="small" />
          <Box className="text-body-strong text-foreground-prominent">{currency}</Box>
        </Box>

        <Box flexCol className="flex-1">
          {transactionLimit && (
            <Box
              flex
              justify="between"
              align="center"
              className="pt-4 pb-2 cursor-pointer"
              onClick={() => onAction?.(PaymentLimitAction.VIEW, transactionLimit)}
            >
              <Box className="text-body-regular-small text-foreground-subdued">
                {t(LIMIT_TYPE_LABELS[transactionLimit.limitType])}
              </Box>
              <Box flex align="center" gap={8}>
                <Box className="text-body-regular-small text-foreground-prominent">
                  {roundNumberWithCommas(transactionLimit.amount, 2)}
                </Box>
                <Icon name="CaretRight" />
              </Box>
            </Box>
          )}

          {dailyLimit && (
            <Box
              flex
              justify="between"
              align="center"
              className="pt-2 pb-2 cursor-pointer"
              onClick={() => onAction?.(PaymentLimitAction.VIEW, dailyLimit)}
            >
              <Box className="text-body-regular-small text-foreground-subdued">
                {t(LIMIT_TYPE_LABELS[dailyLimit.limitType])}
              </Box>
              <Box flex align="center" gap={8}>
                <Box className="text-body-regular-small text-foreground-prominent">
                  {roundNumberWithCommas(dailyLimit.amount, 2)}
                </Box>
                <Icon name="CaretRight" />
              </Box>
            </Box>
          )}

          {!hasBothLimits && <Box className="py-4" />}
        </Box>

        <Box flex justify="center" gap={16} className="pt-8">
          {transactionLimit && (
            <Box flex align="center" gap={8}>
              <Button
                variant="ghost"
                noPadding
                onClick={() => onAction?.(PaymentLimitAction.EDIT, transactionLimit)}
              >
                {t('paymentLimits.editTransactionLimit')}
              </Button>
              <Button
                variant="ghost"
                noPadding
                onClick={() => onAction?.(PaymentLimitAction.EDIT, transactionLimit)}
                iconElement={<Icon name="SimplePencilCustom" />}
              />
              <Button
                variant="ghost"
                noPadding
                loading={isDeleting}
                onClick={() => onAction?.(PaymentLimitAction.DELETE, transactionLimit)}
                iconElement={<Icon name="Trash" />}
              />
            </Box>
          )}
          {dailyLimit && (
            <Box flex align="center" gap={8}>
              <Button
                variant="ghost"
                noPadding
                onClick={() => onAction?.(PaymentLimitAction.EDIT, dailyLimit)}
              >
                {t('paymentLimits.editDailyLimit')}
              </Button>
              <Button
                variant="ghost"
                noPadding
                onClick={() => onAction?.(PaymentLimitAction.EDIT, dailyLimit)}
                iconElement={<Icon name="SimplePencilCustom" />}
              />
              <Button
                variant="ghost"
                noPadding
                loading={isDeleting}
                onClick={() => onAction?.(PaymentLimitAction.DELETE, dailyLimit)}
                iconElement={<Icon name="Trash" />}
              />
            </Box>
          )}
        </Box>
      </Box>
    </Card>
  )
}
